import { MenuItem, Stack } from '@mui/material';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Sector } from '@/domain/impact/impact.entity';
import {
  Category,
  ImpactCo2Answer,
  TransportationModeValues,
  ValuesMobility,
} from '@/domain/impact-co2/entities/question.entity';
import { selectImpactCo2Answers } from '@/domain/impact-co2/impact-co2.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { language, translate } from '@/ui/i18n';
import {
  AddNewQuiz,
  NewQuizEdition,
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/components/quiz-panel/co2/components/QuizComponents';
import TagForm from '@/ui/pages/app/components/quiz-panel/co2/components/TagForm.tsx';
import { useImpactCo2AnswerSubmitDisabled } from '@/ui/pages/app/components/quiz-panel/co2/hooks/useImpactCo2AnswerSubmitDisabled';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { isNumber } from '@/ui/utils/form-validations';

const CATEGORY: Category = 'MOBILITY';

export function MobilityForm({
  sector,
  onSubmit,
  onDelete,
  onEdit,
  isExternal = false,
  initialValues,
  hideDistance = false,
}: {
  sector: Sector;
  onSubmit(answer: ImpactCo2Answer): void;
  onDelete(answer: ImpactCo2Answer): void;
  onEdit(answer: ImpactCo2Answer): void;
  isExternal?: boolean;
  initialValues?: {
    totalDistance?: number;
  };
  hideDistance?: boolean;
}) {
  const { hideCompleted, panelOpen, answersSelectorOverride } = useContext(
    ImpactQuestionContext,
  );
  const [displayNew, setDisplayNew] = useState(false);
  const [displayTag, setDisplayTag] = useState(false);
  const [editing, setEditing] = useState<Array<number>>([]);

  const defaultAnswersSelector = selectImpactCo2Answers<ValuesMobility>({
    sector: sector,
    category: CATEGORY,
    isExternal: isExternal,
  });
  let answersSelector = defaultAnswersSelector;
  if (answersSelectorOverride) {
    answersSelector = answersSelectorOverride;
  }
  const answers: ImpactCo2Answer<ValuesMobility>[] =
    useAppSelector(answersSelector);

  const { disableSubmitActions } = useImpactCo2AnswerSubmitDisabled();

  const handleSubmit = (values: ValuesMobility) => {
    const answer: ImpactCo2Answer = {
      id: -1,
      userId: '',
      sector: sector,
      category: CATEGORY,
      values: values,
      isExternal: isExternal,
      source: '',
    };
    onSubmit(answer);
    setDisplayNew(false);
  };

  const handleEdit = (values: ValuesMobility, id: number) => {
    const answer: ImpactCo2Answer = {
      id,
      userId: '',
      sector,
      category: CATEGORY,
      values,
      isExternal: isExternal,
      source: '',
    };
    onEdit(answer);
    removeFromEditing(answer.id);
  };

  const handleDelete = () => {
    setDisplayNew(false);
  };

  const addToEditing = (id: number) => {
    setEditing([...editing, id]);
  };

  const removeFromEditing = (id: number) => {
    setEditing([...editing].filter((i) => i !== id));
  };

  return (
    <>
      {/* check if we have to hide question if already completed */}
      {!(hideCompleted && answers.length > 0) && (
        <>
          {/* already created answers */}
          {answers
            .sort((a, b) => a.id - b.id)
            .map((answer, i) => (
              <div key={`mobility` + i}>
                {editing.includes(answer.id) && !!panelOpen ? (
                  <QuizEdition
                    answer={answer}
                    removeFromEditing={removeFromEditing}
                    index={i}
                    setDisplayTag={() => setDisplayTag(!displayTag)}
                    title={translate('ImpactQuestion.MOBILITY.question-title')}
                    form={
                      <Form
                        onSubmit={(values) => handleEdit(values, answer.id)}
                        defaultValues={answer.values}
                        onDelete={() => onDelete(answer)}
                        answersCount={answers.length}
                        disableSubmit={disableSubmitActions}
                        disableDelete={disableSubmitActions}
                        isExternal={isExternal}
                        displayTag={displayTag}
                        hideDistance={hideDistance}
                      />
                    }
                  />
                ) : (
                  <QuizNotEdition
                    addToEditing={addToEditing}
                    answeredValues={[
                      translate(
                        `ImpactQuestion.MOBILITY.transportationModeValues.${answer.values.transportationMode}`,
                      ),
                      `${answer.values.passengerCount} ${translate(
                        answer.values.passengerCount > 1
                          ? 'ImpactQuestion.MOBILITY.people'
                          : 'ImpactQuestion.MOBILITY.person',
                      )}`,
                      `${answer.values.travelDistance.toLocaleString(
                        language,
                      )} km`,
                    ]}
                    title={translate('ImpactQuestion.MOBILITY.question-title')}
                    index={i}
                    answer={answer}
                    quizLabel={answer.values.label}
                    score={answer.values.score}
                  />
                )}
              </div>
            ))}

          {/* new answer */}
          {displayNew || answers.length === 0 ? (
            <NewQuizEdition
              answersLength={answers.length}
              title={translate('ImpactQuestion.MOBILITY.question-title')}
              setDisplayTag={() => setDisplayTag(!displayTag)}
              form={
                <Form
                  onSubmit={handleSubmit}
                  defaultValues={{
                    ...emptyFormInitialValues,
                    travelDistance:
                      initialValues?.totalDistance ||
                      emptyFormInitialValues.travelDistance,
                  }}
                  onDelete={() => handleDelete()}
                  answersCount={answers.length}
                  disableSubmit={disableSubmitActions}
                  disableDelete={disableSubmitActions}
                  isExternal={isExternal}
                  displayTag={displayTag}
                  hideDistance={hideDistance}
                />
              }
            />
          ) : (
            <AddNewQuiz
              setDisplayNew={setDisplayNew}
              buttonTitle={translate(
                'ImpactQuestion.MOBILITY.add-button-title',
              )}
            />
          )}
        </>
      )}
    </>
  );
}

const emptyFormInitialValues: ValuesMobility = {
  transportationMode: 'BICYCLE',
  passengerCount: 0,
  travelDistance: 0,
  label: '',
  score: 0,
  tags: [],
};

function Form({
  onSubmit,
  onDelete,
  defaultValues,
  answersCount,
  disableSubmit,
  disableDelete,
  isExternal,
  displayTag,
  hideDistance = false,
}: {
  onSubmit: (values: ValuesMobility) => void;
  onDelete: () => void;
  defaultValues?: ValuesMobility;
  answersCount: number;
  disableSubmit: boolean;
  disableDelete: boolean;
  displayTag: boolean;
  isExternal?: boolean;
  hideDistance?: boolean;
}) {
  const initialValues: ValuesMobility = defaultValues || emptyFormInitialValues;

  const { handleSubmit, register, formState, setValue } =
    useForm<ValuesMobility>({
      defaultValues: initialValues,
    });

  const { errors } = formState;

  const handleFormSubmit = (data: ValuesMobility) => {
    const values = {
      ...data,
      travelDistance: parseFloat(data.travelDistance.toString()),
      passengerCount: parseInt(data.passengerCount.toString()),
    };
    onSubmit(values);
  };

  return (
    <Stack>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack>
          {!isExternal && (displayTag || initialValues.tags.length > 0) && (
            <TagForm
              handleChange={(tags) => setValue('tags', tags)}
              defaultValue={initialValues.tags}
            />
          )}
          <FSTextField
            type="text"
            defaultValue={initialValues.label}
            register={{
              ...register('label'),
            }}
            error={!!errors.label}
            helperText={errors.label?.message}
            placeholder={translate('ImpactQuestion.add-title')}
            placeholderColor="#A29BAE"
            variant="standard"
            sx={{ mb: 2 }}
          />
          <FSTextField
            select
            defaultValue={initialValues.transportationMode}
            label={translate('ImpactQuestion.MOBILITY.transportationModeTitle')}
            register={{
              ...register('transportationMode'),
            }}
            error={!!errors.transportationMode}
            helperText={errors.transportationMode?.message}
          >
            {TransportationModeValues.map((value) => (
              <MenuItem key={value} value={value}>
                {translate(
                  `ImpactQuestion.MOBILITY.transportationModeValues.${value}`,
                )}
              </MenuItem>
            ))}
          </FSTextField>
          <FSTextField
            type="number"
            label={translate('ImpactQuestion.MOBILITY.passengerCountTitle')}
            defaultValue={initialValues.passengerCount}
            register={{
              ...register('passengerCount', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.passengerCount}
            helperText={errors.passengerCount?.message}
          />
          {!hideDistance && (
            <FSTextField
              type="number"
              label={translate('ImpactQuestion.MOBILITY.travelDistanceTitle')}
              defaultValue={initialValues.travelDistance}
              register={{
                ...register('travelDistance', {
                  required: translate('Form.fill-the-field'),
                  validate: isNumber,
                }),
              }}
              error={!!errors.travelDistance}
              helperText={errors.travelDistance?.message}
              step={0.1}
            />
          )}
          <Stack direction={'row'} marginTop={1} gap={1}>
            <FSPrimaryButton type="submit" disabled={disableSubmit}>
              {translate(defaultValues ? 'Form.save' : 'Form.add')}
            </FSPrimaryButton>
            {onDelete && answersCount > 0 && (
              <FSSecondaryButton onClick={onDelete} disabled={disableDelete}>
                {translate('Form.delete')}
              </FSSecondaryButton>
            )}
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
